import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Instance axios optimisée
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 5000, // 5 secondes timeout pour dev
  headers: {
    'Content-Type': 'application/json',
  }
});

// console.log('[API] Client configured with base URL:', API_BASE_URL);

// Intercepteur pour ajouter le token automatiquement
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Intercepteur de réponse pour gérer les erreurs de façon centralisée
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    
    // Si erreur 401, nettoyer le token
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      // Éviter une redirection infinie
      if (window.location.pathname !== '/connexion') {
        window.location.href = '/connexion';
      }
    }
    
    return Promise.reject(error);
  }
);

// Service API optimisé avec mise en cache
export const apiService = {
  // Slots
  getSlots: async (availableOnly = false) => {
    const slots = [];
    const limit = 200;
    for (let skip = 0; ; skip += limit) {
      const response = await apiClient.get(`/api/slots?available_only=${availableOnly}&limit=${limit}&skip=${skip}`);
      slots.push(...response.data);
      if (response.data.length < limit) return slots;
    }
  },

  // Appointments avec optimisation
  getAppointments: async () => {
    const response = await apiClient.get('/api/appointments');
    return response.data;
  },

  createAppointment: async (appointmentData) => {
    const response = await apiClient.post('/api/appointments', appointmentData);
    return response.data;
  },

  updateAppointmentStatus: async (appointmentId, status) => {
    const response = await apiClient.put(`/api/appointments/${appointmentId}/status`, { status });
    return response.data;
  },

  deleteAppointment: async (appointmentId) => {
    await apiClient.delete(`/api/appointments/${appointmentId}`);
    return true;
  },
  cancelAppointment: async (appointmentId) => {
    const response = await apiClient.put(`/api/appointments/${appointmentId}/cancel`);
    return response.data;
  },

  // Reviews avec optimisation
  getApprovedReviews: async () => {
    const response = await apiClient.get('/api/reviews?approved_only=true');
    return response.data;
  },

  getAllReviews: async () => {
    const response = await apiClient.get('/api/reviews');
    return response.data;
  },

  createReview: async (reviewData) => {
    const response = await apiClient.post('/api/reviews', reviewData);
    return response.data;
  },

  updateReviewStatus: async (reviewId, status) => {
    const response = await apiClient.put(`/api/reviews/${reviewId}`, { status });
    return response.data;
  },

  // Slots admin
  createSlot: async (slotData) => {
    const response = await apiClient.post('/api/slots', slotData);
    return response.data;
  },

  deleteSlot: async (slotId) => {
    await apiClient.delete(`/api/slots/${slotId}`);
    return true;
  },

  // Keep-alive
  ping: async () => {
    const response = await apiClient.head('/api/ping');
    return response.status === 200;
  },

  // Batch operations pour réduire les requêtes
  getDashboardData: async (userRole = 'client') => {
    if (userRole === 'admin') {
      const [appointments, slots, reviews] = await Promise.all([
        apiClient.get('/api/appointments'),
        apiClient.get('/api/slots'),
        apiClient.get('/api/reviews')
      ]);
      return {
        appointments: appointments.data,
        slots: slots.data,
        reviews: reviews.data
      };
    } else {
      const [appointments, slots] = await Promise.all([
        apiClient.get('/api/appointments'),
        apiClient.get('/api/slots?available_only=true')
      ]);
      return {
        appointments: appointments.data,
        slots: slots.data
      };
    }
  },

  // Password Reset
  requestPasswordReset: async (email) => {
    const response = await apiClient.post('/api/auth/password-reset/request', { email });
    return response.data;
  },

  confirmPasswordReset: async (email, code, new_password) => {
    const response = await apiClient.post('/api/auth/password-reset/confirm', {
      email,
      code,
      new_password
    });
    return response.data;
  },

  // Maintenance Mode
  getMaintenanceStatus: async () => {
    // console.log('[API] Fetching maintenance status from:', `${API_BASE_URL}/api/maintenance`);
    try {
      const response = await apiClient.get('/api/maintenance');
      // console.log('[API] Maintenance status response:', response.data);
      return response.data;
    } catch (error) {
      console.error('[API] Maintenance status error:', error.message);
      throw error;
    }
  },

  toggleMaintenance: async (is_maintenance, message = '') => {
    const response = await apiClient.post('/api/maintenance', {
      is_maintenance,
      message
    });
    return response.data;
  },

  // Loyalty System
  getLoyaltyCard: async () => {
    const response = await apiClient.get('/api/loyalty/card');
    return response.data;
  },

  validateStamp: async (qrCode) => {
    const response = await apiClient.post('/api/loyalty/validate-stamp', {
      qr_code: qrCode
    });
    return response.data;
  },

  useDiscount: async () => {
    const response = await apiClient.post('/api/loyalty/use-discount');
    return response.data;
  },

  getLoyaltyStats: async () => {
    const response = await apiClient.get('/api/loyalty/stats');
    return response.data;
  },

  getLoyaltyQRImage: async () => {
    const response = await apiClient.get('/api/loyalty/qr-image', {
      responseType: 'blob'
    });
    return response.data;
  },

  // Admin Statistics
  getAdminStatistics: async () => {
    const response = await apiClient.get('/api/admin/statistics');
    return response.data;
  }
};

export default apiService;
